import React, { useEffect, useRef, useState } from "react";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import TablePagination from "@material-ui/core/TablePagination";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import SearchIcon from "@material-ui/icons/Search";
import {
  AZUL,
  BORDA,
  CINZA_BOTAO,
  ETIQUETA_AMARELO_FUNDO,
  ETIQUETA_AMARELO_TEXTO,
  ALERTA_BORDA,
  ALERTA_FUNDO,
  ETIQUETA_ARDOSIA_FUNDO,
  ETIQUETA_ARDOSIA_TEXTO,
  ETIQUETA_CINZA_FUNDO,
  ETIQUETA_CINZA_TEXTO,
  ETIQUETA_VERDE_FUNDO,
  ETIQUETA_VERDE_TEXTO,
  ETIQUETA_VERMELHO_FUNDO,
  ETIQUETA_VERMELHO_TEXTO,
  FONTE,
  FUNDO_CARTAO_PAGAMENTO,
  FUNDO_SUAVE,
  NAVY,
  NAVY_FUNDO,
  AZUL_FUNDO,
  SUAVE,
  TEXTO_TABELA,
  estilos,
  FUNDO_CARTAO,
  TEXTO_SOBRE_COR,
} from "../estilos/tokens";
import { inteiro, moeda, percentual, reais } from "../formatadores";
import { faixaErro } from "./EstadoRequisicao";
import { acionavelPorTeclado } from "./acionavel";
import { PainelAncorado } from "./painelAncorado";
import { rotuloModalidade } from "./modalidade";
import {
  OPCOES_VAZIAS,
  comSelecionados,
  opcoesDaResposta,
} from "./opcoesFiltro";
import { useFiltroHome } from "../contexto/filtroHome";
import { rotuloUnidadePorCodigo } from "../contexto/rotuloUnidade";
import { baixarCsv, montarCsv, textoCsv } from "./csv";
import { useRequisicao } from "../hooks/useRequisicao";
import { useLarguraTela } from "../hooks/useLarguraTela";
import { FiltroHome, FiltroUnidade, TipoTransacao, TransacaoDetalhe, buscarTransacoes } from "../../../../services/home";
import RastreabilidadeTransacao from "./rastreabilidadeTransacao";

const TAMANHOS_PAGINA = [10, 20, 50, 100];
const TAMANHO_INICIAL = TAMANHOS_PAGINA[0];

const OPCOES_STATUS_VENDA = ["Aprovada", "Ajuste", "Cancelada"];
const OPCOES_STATUS_PAGAMENTO = ["Conciliada", "Divergente", "Pendente", "Ajuste", "Cancelada"];

export const opcoesStatus = (tipo: TipoTransacao) => (tipo === "VENDA" ? OPCOES_STATUS_VENDA : OPCOES_STATUS_PAGAMENTO);

export type CorEtiqueta = "verde" | "amarelo" | "vermelho" | "cinza" | "ardosia";

const COR_POR_STATUS: Record<string, CorEtiqueta> = {
  divergente: "vermelho",
  "taxa divergente": "vermelho",
  "bruto divergente": "vermelho",
  "liquido divergente": "vermelho",
  debito: "vermelho",
  pendente: "amarelo",
  ajuste: "cinza",
  "com ajuste": "cinza",
  "ajuste de saldo": "cinza",
  cancelada: "ardosia",
};

export const corEtiqueta = (status: string): CorEtiqueta =>
  COR_POR_STATUS[(status || "").trim().toLowerCase()] || "verde";

const CORES_ETIQUETA: Record<CorEtiqueta, { color: string; background: string }> = {
  verde: { color: ETIQUETA_VERDE_TEXTO, background: ETIQUETA_VERDE_FUNDO },
  amarelo: { color: ETIQUETA_AMARELO_TEXTO, background: ETIQUETA_AMARELO_FUNDO },
  vermelho: { color: ETIQUETA_VERMELHO_TEXTO, background: ETIQUETA_VERMELHO_FUNDO },
  cinza: { color: ETIQUETA_CINZA_TEXTO, background: ETIQUETA_CINZA_FUNDO },
  ardosia: { color: ETIQUETA_ARDOSIA_TEXTO, background: ETIQUETA_ARDOSIA_FUNDO },
};

const capitalizar = (texto: string) => (texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto);

const celula: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 16,
  color: TEXTO_TABELA,
  padding: "13px 12px",
  borderBottom: `1px solid ${BORDA}`,
  whiteSpace: "nowrap",
  textAlign: "center",
};

const alinhamento = (indiceColuna: number): React.CSSProperties =>
  indiceColuna === 0 ? { textAlign: "left" } : {};

const cabecalho: React.CSSProperties = {
  ...celula,
  fontSize: 16,
  fontWeight: 700,
  color: NAVY,
  background: FUNDO_CARTAO,
  padding: "16px 12px",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const celulaAntecipado: React.CSSProperties = { padding: "13px 8px", textAlign: "left" };

const cabecalhoAntecipado: React.CSSProperties = { padding: "16px 8px", whiteSpace: "normal", textAlign: "left" };

// Sem o filtro de status, os tres filtros dividem as 8 colunas que os quatro ocupam nas outras abas
const larguraFiltroAntecipado: React.CSSProperties = { flexBasis: "22.2222%", maxWidth: "22.2222%" };

const barraRecarga: React.CSSProperties = {
  height: 3,
  background: BORDA,
  borderRadius: 3,
  marginBottom: 10,
};

const pilula: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  border: `1px solid ${BORDA}`,
  borderRadius: 28,
  background: FUNDO_CARTAO,
  padding: "5px 6px 5px 18px",
  fontFamily: FONTE,
  boxSizing: "border-box",
  height: 48,
};

export const estiloCaixaBusca = (focada: boolean): React.CSSProperties => ({
  ...pilula,
  padding: "5px 16px",
  cursor: "text",
  boxShadow: focada ? `0 0 0 3px ${FUNDO_SUAVE}` : "none",
});

const rotuloPilula: React.CSSProperties = {
  fontSize: 14.5,
  fontWeight: 600,
  color: SUAVE,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  whiteSpace: "nowrap",
};

const chipValor: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: AZUL_FUNDO,
  color: TEXTO_SOBRE_COR,
  borderRadius: 22,
  padding: "6px 8px 6px 14px",
  fontSize: 16,
  fontWeight: 600,
  maxWidth: 150,
  overflow: "hidden",
};

const SeletorFiltro = ({
  rotulo,
  valor,
  opcoes,
  onSelecionar,
  exibir,
}: {
  rotulo: string;
  valor: string;
  opcoes: string[];
  onSelecionar: (opcao: string) => void;
  exibir?: (opcao: string) => string;
}) => {
  const texto = exibir || ((opcao: string) => opcao);
  const [ancora, setAncora] = useState<null | HTMLElement>(null);
  const fechar = () => setAncora(null);
  const selecionar = (opcao: string) => {
    onSelecionar(opcao);
    fechar();
  };

  return (
    <>
      <div
        style={{ ...pilula, cursor: "pointer" }}
        aria-haspopup="true"
        aria-expanded={Boolean(ancora)}
        aria-label={`${rotulo}: ${valor ? texto(valor) : "Todas"}`}
        {...acionavelPorTeclado((evento) => setAncora(ancora ? null : evento.currentTarget))}
      >
        <span style={rotuloPilula}>{rotulo}</span>
        <span style={chipValor}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {valor ? texto(valor) : "Todas"}
          </span>
          <KeyboardArrowDownIcon style={{ fontSize: 20, flexShrink: 0 }} />
        </span>
      </div>
      <PainelAncorado ancora={ancora} aoFechar={fechar}>
        <MenuList
          autoFocusItem={Boolean(ancora)}
          onKeyDown={(evento) => {
            if (evento.key === "Tab") fechar();
          }}
        >
          <MenuItem onClick={() => selecionar("")}>Todas</MenuItem>
          {opcoes.map((opcao) => (
            <MenuItem key={opcao} selected={opcao === valor} onClick={() => selecionar(opcao)}>
              {texto(opcao)}
            </MenuItem>
          ))}
        </MenuList>
      </PainelAncorado>
    </>
  );
};

const LIMITE_PENDENTES = 0.2;

export const pendentesAcimaDoLimite = (resumo: { totalTransacoes: number; pendentes?: number | null } | null | undefined) =>
  !!resumo && resumo.totalTransacoes > 0 && (resumo.pendentes || 0) / resumo.totalTransacoes > LIMITE_PENDENTES;

const useEstilosAlerta = makeStyles({
  "@keyframes piscarAlerta": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.35 },
  },
  alerta: {
    fontFamily: FONTE,
    fontSize: 15,
    color: NAVY,
    background: ALERTA_FUNDO,
    border: `1px solid ${ALERTA_BORDA}`,
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 14,
    animation: "$piscarAlerta 1s ease-in-out 3",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },
});

function AlertaPendentes() {
  const estilos = useEstilosAlerta();
  return (
    <div role="alert" data-teste="alerta-pendentes" className={estilos.alerta}>
      <strong>Atenção:</strong> Mais de 20% da sua movimentação neste período não está conciliada. Isso pode ocorrer
      devido à ausência de registros de venda ou pagamento enviados pela operadora, impactando a precisão das
      informações. Abra um atendimento para verificar as divergências e regularizar os registros.
    </div>
  );
}

export const estiloCartaoIndicador = (fundo?: string, status?: string): React.CSSProperties => {
  const fundoStatus = status ? CORES_ETIQUETA[corEtiqueta(status)].background : undefined;
  return {
    ...estilos.cartao,
    padding: "16px 20px",
    background: fundoStatus || fundo || estilos.cartao.background,
    minWidth: 0,
    ...(fundoStatus ? { borderColor: fundoStatus } : {}),
  };
};

const reaisOuTraco = (valor: number | null) => (valor === null || valor === undefined ? "—" : reais(valor));

const CartaoIndicador = ({
  rotulo,
  valor,
  fundo,
  status,
  descricao,
}: {
  rotulo: string;
  valor: string;
  fundo?: string;
  status?: string;
  descricao?: string;
}) => {
  const cartao = (
    <div
      data-status={status}
      style={{ ...estiloCartaoIndicador(fundo, status), cursor: descricao ? "help" : undefined }}
    >
      <div
        style={{
          fontFamily: FONTE,
          fontSize: 14.5,
          fontWeight: 600,
          color: SUAVE,
          textTransform: "uppercase",
          letterSpacing: 0.7,
          marginBottom: 8,
          ...(descricao ? { display: "flex", alignItems: "center", gap: 6 } : {}),
        }}
      >
        {rotulo}
        {descricao ? (
          <InfoOutlined data-teste="ajuda-indicador" aria-hidden style={{ fontSize: 17, color: SUAVE }} />
        ) : null}
      </div>
      <div
        style={{
          fontFamily: FONTE,
          fontSize: "clamp(21px, 1.6vw, 28px)",
          fontWeight: 700,
          color: NAVY,
          overflowWrap: "anywhere",
        }}
      >
        {valor}
      </div>
    </div>
  );
  return descricao ? (
    <Tooltip
      title={
        <span data-teste="texto-ajuda-indicador" style={estilos.textoAjudaIndicador}>
          {descricao}
        </span>
      }
      arrow
      placement="top"
    >
      {cartao}
    </Tooltip>
  ) : (
    cartao
  );
};

const Etiqueta = ({ status }: { status: string }) => {
  const texto = capitalizar(status);
  return (
    <span
      data-status={status}
      title={texto}
      style={{
        display: "inline-block",
        width: 150,
        boxSizing: "border-box",
        textAlign: "center",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        borderRadius: 14,
        padding: "4px 10px",
        fontFamily: FONTE,
        fontSize: 15,
        fontWeight: 600,
        ...CORES_ETIQUETA[corEtiqueta(status)],
      }}
    >
      {texto}
    </span>
  );
};

type Faixa = "sempre" | "media" | "ampla";

interface Coluna {
  chave: string;
  titulo: string;
  faixa: Faixa;
  monetaria?: boolean;
  descricao?: string;
  conteudo: (transacao: TransacaoDetalhe) => React.ReactNode;
  csv: (transacao: TransacaoDetalhe) => string;
}

const COLUNAS: Coluna[] = [
  {
    chave: "dataHora",
    titulo: "Data / Hora",
    faixa: "sempre",
    conteudo: (t) => t.dataHora,
    csv: (t) => textoCsv(t.dataHora),
  },
  { chave: "nsu", titulo: "NSU", faixa: "sempre", conteudo: (t) => t.nsu, csv: (t) => textoCsv(t.nsu) },
  {
    chave: "autorizacao",
    titulo: "Autorização",
    faixa: "ampla",
    conteudo: (t) => t.autorizacao,
    csv: (t) => textoCsv(t.autorizacao),
  },
  {
    chave: "bandeira",
    titulo: "Bandeira",
    faixa: "media",
    conteudo: (t) => t.bandeira,
    csv: (t) => textoCsv(t.bandeira),
  },
  {
    chave: "modalidade",
    titulo: "Modalidade",
    faixa: "media",
    conteudo: (t) => rotuloModalidade(t.modalidade),
    csv: (t) => textoCsv(rotuloModalidade(t.modalidade)),
  },
  {
    chave: "parcelas",
    titulo: "Parcelas",
    faixa: "ampla",
    conteudo: (t) => t.parcelas,
    csv: (t) => textoCsv(t.parcelas),
  },
  {
    chave: "valorBruto",
    titulo: "Valor Bruto",
    faixa: "sempre",
    monetaria: true,
    conteudo: (t) => reais(t.valorBruto),
    csv: (t) => moeda(t.valorBruto),
  },
  {
    chave: "valorLiquido",
    titulo: "Valor Líquido",
    faixa: "sempre",
    monetaria: true,
    conteudo: (t) => reais(t.valorLiquido),
    csv: (t) => moeda(t.valorLiquido),
  },
  {
    chave: "taxaPercentual",
    titulo: "Taxa",
    faixa: "ampla",
    conteudo: (t) => percentual(t.taxaPercentual),
    csv: (t) => moeda(t.taxaPercentual),
  },
  {
    chave: "taxaValor",
    titulo: "Valor da Taxa",
    faixa: "media",
    conteudo: (t) => reais(t.taxaValor),
    csv: (t) => moeda(t.taxaValor),
  },
  {
    chave: "status",
    titulo: "Status",
    faixa: "sempre",
    conteudo: (t) => <Etiqueta status={t.status} />,
    csv: (t) => textoCsv(capitalizar(t.status)),
  },
];

const COLUNAS_ANTECIPADO: Coluna[] = [
  {
    chave: "dataHora",
    titulo: "Data / Hora",
    faixa: "sempre",
    descricao: "Data e hora em que a venda foi realizada.",
    conteudo: (t) => t.dataHora,
    csv: (t) => textoCsv(t.dataHora),
  },
  {
    chave: "nsu",
    titulo: "NSU",
    faixa: "sempre",
    descricao: "Número sequencial único que identifica a venda na operadora.",
    conteudo: (t) => t.nsu,
    csv: (t) => textoCsv(t.nsu),
  },
  {
    chave: "autorizacao",
    titulo: "Autorização",
    faixa: "ampla",
    descricao: "Código de autorização da venda emitido pela operadora.",
    conteudo: (t) => t.autorizacao,
    csv: (t) => textoCsv(t.autorizacao),
  },
  {
    chave: "bandeira",
    titulo: "Bandeira",
    faixa: "ampla",
    descricao: "Bandeira do cartão usado na venda.",
    conteudo: (t) => t.bandeira,
    csv: (t) => textoCsv(t.bandeira),
  },
  {
    chave: "modalidade",
    titulo: "Modalidade",
    faixa: "ampla",
    descricao: "Forma de pagamento da venda: débito, crédito à vista ou crédito parcelado.",
    conteudo: (t) => rotuloModalidade(t.modalidade),
    csv: (t) => textoCsv(rotuloModalidade(t.modalidade)),
  },
  {
    chave: "parcelas",
    titulo: "Parcela",
    faixa: "media",
    descricao: "Parcela antecipada em relação ao total de parcelas da venda.",
    conteudo: (t) => t.parcelas,
    csv: (t) => textoCsv(t.parcelas),
  },
  {
    chave: "diasAntecipados",
    titulo: "Dias antecipados",
    faixa: "media",
    descricao: "Quantidade de dias entre o pagamento antecipado e o vencimento original da parcela.",
    conteudo: (t) => inteiro(t.diasAntecipados),
    csv: (t) => inteiro(t.diasAntecipados),
  },
  {
    chave: "valorBruto",
    titulo: "Valor bruto",
    faixa: "sempre",
    monetaria: true,
    descricao: "Valor bruto antecipado da parcela, em reais, antes dos descontos.",
    conteudo: (t) => moeda(t.valorBruto),
    csv: (t) => moeda(t.valorBruto),
  },
  {
    chave: "taxaAntecipacao",
    titulo: "Taxa de antecipação",
    faixa: "sempre",
    descricao: "Percentual cobrado pela operadora para antecipar a parcela.",
    conteudo: (t) => moeda(t.taxaAntecipacao),
    csv: (t) => moeda(t.taxaAntecipacao),
  },
  {
    chave: "taxaTotal",
    titulo: "Taxa total",
    faixa: "ampla",
    descricao: "Percentual total descontado da parcela, somando a taxa da venda e a de antecipação.",
    conteudo: (t) => moeda(t.taxaTotal),
    csv: (t) => moeda(t.taxaTotal),
  },
  {
    chave: "taxaValor",
    titulo: "Desconto total",
    faixa: "media",
    monetaria: true,
    descricao: "Valor total descontado da parcela, em reais.",
    conteudo: (t) => moeda(t.taxaValor),
    csv: (t) => moeda(t.taxaValor),
  },
  {
    chave: "valorLiquido",
    titulo: "Valor líquido",
    faixa: "sempre",
    monetaria: true,
    descricao: "Valor líquido antecipado da parcela, em reais, já com os descontos.",
    conteudo: (t) => moeda(t.valorLiquido),
    csv: (t) => moeda(t.valorLiquido),
  },
];

const colunaUnidade = (unidades: FiltroUnidade[]): Coluna => ({
  chave: "unidade",
  titulo: "Unidade",
  faixa: "ampla",
  conteudo: (t) => rotuloUnidadePorCodigo(t.codPessoa, unidades),
  csv: (t) => textoCsv(rotuloUnidadePorCodigo(t.codPessoa, unidades)),
});

const TituloColuna = ({ coluna }: { coluna: Coluna }) =>
  coluna.descricao ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {coluna.titulo}
      <Tooltip
        title={<span style={estilos.textoAjudaIndicador}>{coluna.descricao}</span>}
        arrow
        placement="top"
      >
        <InfoOutlined
          data-teste="ajuda-coluna"
          aria-label={coluna.descricao}
          style={{ fontSize: 16, color: SUAVE, cursor: "help", flexShrink: 0 }}
        />
      </Tooltip>
    </span>
  ) : (
    <>{coluna.titulo}</>
  );

const TAMANHO_LOTE_EXPORTACAO = 10000;
const LIMITE_EXPORTACAO = 100000;

const colunasPorTipo = (tipo: TipoTransacao) => (tipo === "ANTECIPADO" ? COLUNAS_ANTECIPADO : COLUNAS);

const BotaoDetalhes = ({ aoAbrir }: { aoAbrir: () => void }) => (
  <span
    {...acionavelPorTeclado(aoAbrir)}
    aria-label="Ver detalhes da transação"
    title="Ver detalhes"
    style={{ display: "inline-flex", cursor: "pointer", color: AZUL }}
  >
    <SearchIcon fontSize="small" />
  </span>
);

const rotuloCampo: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 13.5,
  fontWeight: 600,
  color: SUAVE,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  marginBottom: 2,
};

const valorCampo: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 16,
  color: TEXTO_TABELA,
  wordBreak: "break-word",
};

const CartaoTransacao = ({
  transacao,
  colunas,
  aoAbrir,
}: {
  transacao: TransacaoDetalhe;
  colunas: Coluna[];
  aoAbrir: () => void;
}) => {
  const campos = colunas.filter((coluna) => coluna.chave !== "dataHora" && coluna.chave !== "status");

  return (
    <div style={{ ...estilos.cartao, height: "auto", padding: 16, marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <span style={{ fontFamily: FONTE, fontSize: 17, fontWeight: 700, color: NAVY, whiteSpace: "nowrap" }}>
          {transacao.dataHora}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Etiqueta status={transacao.status} />
          <BotaoDetalhes aoAbrir={aoAbrir} />
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {campos.map((coluna) => (
          <div key={coluna.chave} style={{ flex: "1 1 45%", minWidth: 120, marginBottom: 10 }}>
            <div style={rotuloCampo}>{coluna.titulo}</div>
            <div
              style={valorCampo}
            >
              {coluna.conteudo(transacao)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const exportarCsv = (transacoes: TransacaoDetalhe[], colunas: Coluna[]) =>
  baixarCsv(
    "transacoes",
    montarCsv(
      colunas.map((coluna) => coluna.titulo),
      transacoes.map((t) => colunas.map((coluna) => coluna.csv(t)))
    )
  );

const TabelaTransacoes = ({ tipo, filtro }: { tipo: TipoTransacao; filtro: FiltroHome }) => {
  const { unidades } = useFiltroHome();
  const { celular, compacto } = useLarguraTela();
  const colunas = colunasPorTipo(tipo);
  const antecipado = tipo === "ANTECIPADO";

  const [pagina, setPagina] = useState(0);
  const [tamanho, setTamanho] = useState(TAMANHO_INICIAL);
  const [textoBusca, setTextoBusca] = useState("");
  const [buscaFocada, setBuscaFocada] = useState(false);
  const [busca, setBusca] = useState("");
  const [operadora, setOperadora] = useState("");
  const [bandeira, setBandeira] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [status, setStatus] = useState("");
  const [exportando, setExportando] = useState(false);
  const [avisoExportacao, setAvisoExportacao] = useState("");

  const montado = useRef(true);
  useEffect(() => () => {
    montado.current = false;
  }, []);

  const aplicarBusca = () => {
    const novaBusca = textoBusca.trim();
    if (novaBusca !== busca) {
      setBusca(novaBusca);
      setPagina(0);
    }
  };

  const zerarPagina = <T,>(aplicar: (valor: T) => void) => (valor: T) => {
    aplicar(valor);
    setPagina(0);
  };

  const [filtroAnterior, setFiltroAnterior] = useState(filtro);
  const [linhaSelecionada, setLinhaSelecionada] = useState<TransacaoDetalhe | null>(null);
  if (filtroAnterior !== filtro) {
    setFiltroAnterior(filtro);
    setPagina(0);
    setTextoBusca("");
    setBusca("");
    setOperadora("");
    setBandeira("");
    setModalidade("");
    setStatus("");
    setLinhaSelecionada(null);
  }

  const filtroTabela = {
    pagina,
    tamanho,
    busca,
    operadora,
    bandeira,
    modalidade,
    status,
  };

  // O resumo agrega o conjunto filtrado inteiro e nao muda ao paginar; separar as
  // duas requisicoes evita reexecutar a agregacao a cada troca de pagina.
  const {
    dados: dadosResumo,
    carregando: carregandoResumo,
    erro: erroResumo,
    recarregar: recarregarResumo,
  } = useRequisicao(
    // O resumo nao traz linhas: fixar pagina e tamanho impede que os parametros
    // enviados variem sem a chave do hook variar junto.
    () => buscarTransacoes(tipo, filtro, { ...filtroTabela, pagina: 0, tamanho: TAMANHO_INICIAL, incluir: "resumo" }),
    [tipo, filtro, busca, operadora, bandeira, modalidade, status]
  );

  const {
    dados,
    carregando: carregandoLista,
    erro: erroLista,
    recarregar: recarregarLista,
  } = useRequisicao(
    () => buscarTransacoes(tipo, filtro, { ...filtroTabela, incluir: "lista" }),
    [tipo, filtro, pagina, tamanho, busca, operadora, bandeira, modalidade, status]
  );

  // As opcoes dos filtros saem do filtro primario, nao da pagina carregada: a
  // chave omite os filtros secundarios de proposito, senao escolher GETNET
  // reduziria o proprio seletor a GETNET.
  const { dados: dadosOpcoes } = useRequisicao(
    () =>
      buscarTransacoes(tipo, filtro, {
        ...filtroTabela,
        pagina: 0,
        tamanho: TAMANHO_INICIAL,
        busca: "",
        operadora: "",
        bandeira: "",
        modalidade: "",
        status: "",
        incluir: "opcoes",
      }),
    [tipo, filtro]
  );

  const carregando = carregandoLista || carregandoResumo;
  const erro = erroLista || erroResumo;
  const resumoAtual = dadosResumo;
  // O total so chega na resposta do resumo, que e mais lenta que a da lista.
  const totalConhecido = resumoAtual != null;
  const totalRegistros = resumoAtual ? resumoAtual.totalRegistros : 0;
  const linhasNaPagina = dados ? dados.transacoes.length : 0;
  const exportacaoBloqueada =
    exportando || (totalConhecido ? totalRegistros === 0 : dados == null || linhasNaPagina === 0);
  // O MUI 4.8.0 nao entende count -1 (total desconhecido): trava o botao de avancar
  // e imprime "1--1" no rodape. Enquanto o resumo nao chega, um total provisorio
  // afirma existir mais uma pagina apenas quando a atual veio cheia.
  // O piso mantem a pagina corrente dentro do alcance do MUI quando a lista volta
  // vazia: com count = pagina * tamanho o proprio page vira invalido.
  const totalPaginacao = totalConhecido
    ? totalRegistros
    : Math.max(pagina * tamanho + linhasNaPagina + (linhasNaPagina === tamanho ? 1 : 0), pagina * tamanho + 1);
  const intervaloDesconhecido = linhasNaPagina === 0;
  // O total provisorio afirma existir uma pagina a mais sempre que a atual veio
  // cheia, entao a ultima pagina pode ser ultrapassada antes do resumo chegar.
  const ultimaPagina = totalConhecido ? Math.max(0, Math.ceil(totalRegistros / tamanho) - 1) : null;
  const paginaExibida = ultimaPagina != null ? Math.min(pagina, ultimaPagina) : pagina;

  useEffect(() => {
    if (ultimaPagina != null && pagina > ultimaPagina) {
      setPagina(ultimaPagina);
    }
  }, [ultimaPagina, pagina]);
  const recarregar = () => {
    recarregarLista();
    recarregarResumo();
  };

  // Enquanto as opcoes do servidor nao chegam o seletor fica vazio: preencher com
  // as linhas da pagina exibia so parte das operadoras e parecia o filtro completo.
  const opcoesServidor = opcoesDaResposta(dadosOpcoes ? dadosOpcoes.opcoes : null);
  const opcoes = comSelecionados(opcoesServidor || OPCOES_VAZIAS, { operadora, bandeira, modalidade });

  const limparFiltros = () => {
    setTextoBusca("");
    setBusca("");
    setOperadora("");
    setBandeira("");
    setModalidade("");
    setStatus("");
    setPagina(0);
  };

  const buscarTodasParaExportar = async () => {
    const lote = { ...filtroTabela, tamanho: TAMANHO_LOTE_EXPORTACAO };
    const primeira = await buscarTransacoes(tipo, filtro, { ...lote, pagina: 0 });
    const total = primeira.totalRegistros;
    const transacoes = [...primeira.transacoes];
    let ultimoLote = primeira.transacoes.length;
    for (
      let pagina = 1;
      montado.current &&
      ultimoLote === TAMANHO_LOTE_EXPORTACAO &&
      transacoes.length < Math.min(total, LIMITE_EXPORTACAO);
      pagina++
    ) {
      const resposta = await buscarTransacoes(tipo, filtro, { ...lote, pagina, incluir: "lista" });
      ultimoLote = resposta.transacoes.length;
      transacoes.push(...resposta.transacoes);
    }
    return { transacoes, total };
  };

  const exportar = () => {
    setExportando(true);
    setAvisoExportacao("");
    buscarTodasParaExportar()
      .then(({ transacoes, total }) => {
        if (!montado.current) {
          return;
        }
        exportarCsv(transacoes, [colunaUnidade(unidades)].concat(colunas));
        if (total > transacoes.length) {
          setAvisoExportacao(
            `A exportação trouxe as primeiras ${inteiro(transacoes.length)} de ${inteiro(
              total
            )} transações. Reduza o período ou refine os filtros para exportar o restante.`
          );
        }
      })
      .catch(() => {
        if (montado.current) {
          setAvisoExportacao("Não foi possível exportar as transações. Tente novamente.");
        }
      })
      .then(() => {
        if (montado.current) {
          setExportando(false);
        }
      });
  };

  const colunasVisiveis = colunas.filter((coluna) => !compacto || coluna.faixa !== "ampla");
  const estiloCelula = antecipado ? { ...celula, ...celulaAntecipado } : celula;
  const estiloCabecalho = antecipado ? { ...cabecalho, ...cabecalhoAntecipado } : cabecalho;
  const estiloItemFiltro = antecipado && !compacto ? larguraFiltroAntecipado : undefined;

  const resumo = resumoAtual && resumoAtual.resumo;

  return (
    <div>
      {tipo === "PAGAMENTO" && pendentesAcimaDoLimite(resumo) && <AlertaPendentes />}
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        {antecipado ? (
          <>
            <Grid item xs={12} sm={6} md={4} lg>
              <CartaoIndicador rotulo="Total de antecipações" valor={resumo ? inteiro(resumo.totalTransacoes) : "-"} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg>
              <CartaoIndicador rotulo="Valor bruto antecipado" valor={resumo ? reais(resumo.valorBruto) : "-"} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg>
              <CartaoIndicador
                rotulo="Taxa média de antecipação"
                valor={resumo ? percentual(resumo.taxaMediaAntecipacao) : "-"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg>
              <CartaoIndicador rotulo="Taxa média total" valor={resumo ? percentual(resumo.taxaMediaTotal) : "-"} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg>
              <CartaoIndicador rotulo="Valor líquido antecipado" valor={resumo ? reais(resumo.valorLiquido) : "-"} />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4} lg>
              <CartaoIndicador rotulo="Total de transações" valor={resumo ? inteiro(resumo.totalTransacoes) : "-"} />
            </Grid>
            {tipo === "PAGAMENTO" ? (
              <>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador rotulo="Conciliados" status="Conciliada" valor={resumo ? inteiro(resumo.conciliados) : "-"} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador rotulo="Divergentes" status="Divergente" valor={resumo ? inteiro(resumo.divergentes) : "-"} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador rotulo="Cancelamentos" status="Cancelada" valor={resumo ? inteiro(resumo.cancelamentos) : "-"} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador
                    rotulo="Pendentes"
                    status="Pendente"
                    valor={resumo ? inteiro(resumo.pendentes) : "-"}
                    descricao="Transações sem registro de venda ou de pagamento, impossibilitando a conciliação."
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador rotulo="Valor bruto" valor={resumo ? reais(resumo.valorBruto) : "-"} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador rotulo="Valor líquido" valor={resumo ? reais(resumo.valorLiquido) : "-"} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador rotulo="Cancelamentos" valor={resumo ? inteiro(resumo.cancelamentos) : "-"} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg>
                  <CartaoIndicador
                    rotulo="Média de taxa por transação"
                    valor={resumo ? reais(resumo.mediaTaxaPorTransacao) : "-"}
                  />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>

      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
        <Grid item xs={12} sm={6} md={4} lg={2} style={estiloItemFiltro}>
          <SeletorFiltro
            rotulo="Operadora"
            valor={operadora}
            opcoes={opcoes.operadoras}
            onSelecionar={zerarPagina(setOperadora)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2} style={estiloItemFiltro}>
          <SeletorFiltro
            rotulo="Bandeira"
            valor={bandeira}
            opcoes={opcoes.bandeiras}
            onSelecionar={zerarPagina(setBandeira)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2} style={estiloItemFiltro}>
          <SeletorFiltro
            rotulo="Modalidade"
            valor={modalidade}
            opcoes={opcoes.modalidades}
            onSelecionar={zerarPagina(setModalidade)}
            exibir={rotuloModalidade}
          />
        </Grid>
        {!antecipado && (
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <SeletorFiltro
              rotulo="Status"
              valor={status}
              opcoes={opcoesStatus(tipo)}
              onSelecionar={zerarPagina(setStatus)}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4} lg>
          <div style={estiloCaixaBusca(buscaFocada)}>
            <input
              value={textoBusca}
              onChange={(evento) => setTextoBusca(evento.target.value)}
              onFocus={() => setBuscaFocada(true)}
              onBlur={() => setBuscaFocada(false)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter") {
                  aplicarBusca();
                }
              }}
              placeholder="NSU, TID, autorização, valor"
              aria-label="Buscar por NSU, TID ou autorização"
              style={{
                flex: 1,
                minWidth: 0,
                border: "none",
                outline: "none",
                fontFamily: FONTE,
                fontSize: 16,
                color: NAVY,
                background: "transparent",
              }}
            />
            <span
              {...acionavelPorTeclado(aplicarBusca)}
              aria-label="Buscar"
              title="Buscar"
              style={{ display: "inline-flex", cursor: "pointer", flexShrink: 0 }}
            >
              <SearchIcon style={{ fontSize: 22, color: AZUL }} />
            </span>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg="auto">
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button
              onClick={limparFiltros}
              style={{
                fontFamily: FONTE,
                background: FUNDO_CARTAO,
                color: NAVY,
                border: `1px solid ${BORDA}`,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: 24,
                padding: "8px 18px",
                boxShadow: "none",
                whiteSpace: "nowrap",
              }}
            >
              Limpar filtros
            </Button>
            <Button
              onClick={exportar}
              disabled={exportacaoBloqueada}
              style={{
                fontFamily: FONTE,
                background: exportacaoBloqueada ? BORDA : NAVY_FUNDO,
                color: exportacaoBloqueada ? CINZA_BOTAO : TEXTO_SOBRE_COR,
                cursor: exportacaoBloqueada ? "not-allowed" : "pointer",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: 24,
                padding: "8px 26px",
                boxShadow: "none",
                whiteSpace: "nowrap",
              }}
            >
              {exportando ? "Exportando..." : "Exportar"}
            </Button>
          </div>
        </Grid>
      </Grid>

      {tipo === "PAGAMENTO" && (
        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          <Grid item xs={12} sm={6} md={4} lg>
            <CartaoIndicador rotulo="Valor esperado" valor={resumo ? reaisOuTraco(resumo.valorCliente) : "-"} fundo={FUNDO_CARTAO_PAGAMENTO} />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg>
            <CartaoIndicador rotulo="Desconto previsto" valor={resumo ? reais(resumo.taxaCliente) : "-"} fundo={FUNDO_CARTAO_PAGAMENTO} />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg>
            <CartaoIndicador rotulo="Valor operadora" valor={resumo ? reais(resumo.valorOperadora) : "-"} fundo={FUNDO_CARTAO_PAGAMENTO} />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg>
            <CartaoIndicador rotulo="Taxa operadora" valor={resumo ? reais(resumo.taxaOperadora) : "-"} fundo={FUNDO_CARTAO_PAGAMENTO} />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg>
            <CartaoIndicador rotulo="Diferença" valor={resumo ? reaisOuTraco(resumo.diferenca) : "-"} fundo={FUNDO_CARTAO_PAGAMENTO} />
          </Grid>
        </Grid>
      )}

      {avisoExportacao !== "" && (
        <div
          style={{
            fontFamily: FONTE,
            fontSize: 15,
            color: NAVY,
            background: FUNDO_SUAVE,
            border: `1px solid ${BORDA}`,
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
          }}
        >
          {avisoExportacao}
        </div>
      )}

      {carregando && !dados && (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress style={{ color: AZUL }} />
        </div>
      )}

      {carregando && dados && <LinearProgress style={barraRecarga} />}

      {erro && dados && (
        <div style={faixaErro}>
          <span>{erro}</span>
          <Button
            variant="outlined"
            size="small"
            onClick={recarregar}
            style={{ fontFamily: FONTE, color: AZUL, borderColor: AZUL, textTransform: "none" }}
          >
            Tentar novamente
          </Button>
        </div>
      )}

      {erro && !dados && (
        <div style={{ textAlign: "center", padding: 30, fontFamily: FONTE }}>
          <div style={{ fontSize: 17, color: NAVY, marginBottom: 12 }}>{erro}</div>
          <Button
            variant="outlined"
            onClick={recarregar}
            style={{ fontFamily: FONTE, color: AZUL, borderColor: AZUL, textTransform: "none" }}
          >
            Tentar novamente
          </Button>
        </div>
      )}

      {!erro && dados && dados.transacoes.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, fontFamily: FONTE, fontSize: 17, color: SUAVE }}>
          {pagina > 0
            ? "Nenhuma transação nesta página."
            : "Nenhuma transação para o período e filtros selecionados."}
        </div>
      )}

      {dados && dados.transacoes.length > 0 && (
        <>
          {celular ? (
            <div>
              {dados.transacoes.map((t) => (
                <CartaoTransacao
                  key={t.codLinha}
                  transacao={t}
                  colunas={colunas}
                  aoAbrir={() => setLinhaSelecionada(t)}
                />
              ))}
            </div>
          ) : (
            <div style={{ ...estilos.cartao, padding: "6px 18px", overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {colunasVisiveis.map((coluna, indiceColuna) => (
                      <th key={coluna.chave} style={{ ...estiloCabecalho, ...alinhamento(indiceColuna) }}>
                        <TituloColuna coluna={coluna} />
                      </th>
                    ))}
                    <th style={estiloCabecalho}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.transacoes.map((t, indice) => {
                    const ultima = indice === dados.transacoes.length - 1;
                    const linha: React.CSSProperties = ultima ? { borderBottom: "none" } : {};
                    return (
                      <tr key={t.codLinha}>
                        {colunasVisiveis.map((coluna, indiceColuna) => (
                          <td
                            key={coluna.chave}
                            style={{ ...estiloCelula, ...linha, ...alinhamento(indiceColuna) }}
                          >
                            {coluna.conteudo(t)}
                          </td>
                        ))}
                        <td style={{ ...estiloCelula, ...linha }}>
                          <BotaoDetalhes aoAbrir={() => setLinhaSelecionada(t)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Fora do bloco acima: uma pagina alem do fim volta vazia e sem a paginacao
          nao haveria como voltar. */}
      {(pagina > 0 || (dados != null && dados.transacoes.length > 0)) && (
        <TablePagination
          component="div"
          count={totalPaginacao}
          page={paginaExibida}
          rowsPerPage={tamanho}
          rowsPerPageOptions={TAMANHOS_PAGINA}
          onPageChange={(evento, novaPagina) => setPagina(novaPagina)}
          onRowsPerPageChange={(evento) => zerarPagina(setTamanho)(Number(evento.target.value))}
          backIconButtonProps={{ "aria-label": "Página anterior" }}
          nextIconButtonProps={{ "aria-label": "Próxima página" }}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to }) => {
            const intervalo = intervaloDesconhecido ? "..." : `${from}-${to}`;
            return totalConhecido ? `${intervalo} de ${totalRegistros}` : intervalo;
          }}
          style={{ fontFamily: FONTE, color: NAVY }}
        />
      )}

      <RastreabilidadeTransacao
        codLinha={linhaSelecionada ? linhaSelecionada.codLinha : null}
        origem={linhaSelecionada ? linhaSelecionada.origem : undefined}
        tipo={tipo}
        aberto={linhaSelecionada != null}
        aoFechar={() => setLinhaSelecionada(null)}
      />
    </div>
  );
};

export default TabelaTransacoes;
